import React, { useEffect, useMemo } from 'react';
import { DeepLinkTabs } from '../../utils/deepLink/DeepLinkTabs';
import { useDeepLinkParam } from '../../utils/deepLink/useDeepLinkSegment';
import { useDispatch, useSelector } from 'react-redux';
import {
    Content,
    Dialog,
    DialogTrigger,
    Divider,
    Flex,
    Heading,
    Item,
    Picker,
    ProgressCircle,
    Section,
    TabList,
    TabPanels,
    Text,
    View,
    ActionButton,
} from '@adobe/react-spectrum';
import { SearedLoader } from '../genericLoadingDialog/SearedLoader';
import FlashOn from '@spectrum-icons/workflow/FlashOn';
import DataMapping from '@spectrum-icons/workflow/DataMapping';
import Bookmark from '@spectrum-icons/workflow/Bookmark';
import ColorPalette from '@spectrum-icons/workflow/ColorPalette';
import HotFixes from '@spectrum-icons/workflow/HotFixes';
import {
    DEFAULT_BIN_KEY,
    fetchCommanderStatsHealth,
    selectCommanderBin,
} from '../../data/redux/slices/commanderCardStatsSlice';
import { CardsTab } from './commanderCardStatsPanes/CardsTab';
import { CombosTab } from './commanderCardStatsPanes/CombosTab';
import { SynergyTab } from './commanderCardStatsPanes/SynergyTab';
import { ManabaseTab } from './commanderCardStatsPanes/ManabaseTab';
import { ArchetypesTab } from './commanderCardStatsPanes/ArchetypesTab';
import { SaltTab } from './commanderCardStatsPanes/SaltTab';
import './CommanderCardStatsPanel.css';
import { useOpenCardInfo } from '../common/cardInfoDialog/useOpenCardInfo';
import Card from '@spectrum-icons/workflow/Card';
import Beaker from '@spectrum-icons/workflow/Beaker';

const BIN_SECTIONS = [
    {
        id: 'brackets',
        name: 'Bracket',
        children: [
            { key: 'bracket_1', label: 'Bracket 1' },
            { key: 'bracket_2', label: 'Bracket 2' },
            { key: 'bracket_3', label: 'Bracket 3' },
            { key: 'bracket_4', label: 'Bracket 4' },
            { key: 'bracket_5', label: 'Bracket 5' },
        ],
    },
    {
        id: 'power',
        name: 'Power Level',
        children: [
            { key: 'power_1_3', label: 'Power 1–3' },
            { key: 'power_3_5', label: 'Power 3–5' },
            { key: 'power_5_7', label: 'Power 5–7' },
            { key: 'power_7_9', label: 'Power 7–9' },
            { key: 'power_9_10', label: 'Power 9–10' },
        ],
    },
];

const bracketFromRating = (rating) => {
    if (rating == null) return null;
    const rounded = Math.round(parseFloat(rating));
    if (Number.isNaN(rounded)) return null;
    const clamped = Math.max(1, Math.min(5, rounded));
    return `bracket_${clamped}`;
};

export function CommanderCardStatsPanel() {
    const dispatch = useDispatch();

    const commanderIndexId = useSelector((state) => state.commanderDetails?.details?.id);
    const bracketRating = useSelector((state) => state.commanderDetails?.details?.bracketRating);

    const selectedBinKey = useSelector((state) => state.commanderCardStats.selectedBinKey);
    const responsesByBin = useSelector((state) => state.commanderCardStats.responsesByBin);
    const fetchingBins = useSelector((state) => state.commanderCardStats.fetchingBins);
    const isDisabled = useSelector((state) => state.commanderCardStats.isDisabled);
    const storedCommanderId = useSelector((state) => state.commanderCardStats.commanderIndexId);
    const health = useSelector((state) =>
        commanderIndexId ? state.commanderCardStats.healthById[commanderIndexId] : null
    );
    const healthIsFetching = useSelector((state) =>
        commanderIndexId ? !!state.commanderCardStats.healthFetching[commanderIndexId] : false
    );

    const handleSelectCard = useOpenCardInfo();

    const smartDefaultBin = bracketFromRating(bracketRating) || DEFAULT_BIN_KEY;
    // Unscoped: the panel is wrapped in a mobile tab layout but not a
    // desktop one; keep the `bin` URL identical across viewports.
    const [binParam, setBinParam] = useDeepLinkParam('bin', smartDefaultBin, { scope: false });

    // Health probe first — tells us which bins have data and whether the
    // commander has been aggregated at all. All bin fetches are gated on this.
    useEffect(() => {
        if (commanderIndexId) dispatch(fetchCommanderStatsHealth(commanderIndexId));
    }, [commanderIndexId, dispatch]);

    const availableBins = health?.availableBins || {};

    const availableBinSections = useMemo(() => {
        if (!health) return BIN_SECTIONS;
        return BIN_SECTIONS.map((section) => ({
            ...section,
            children: section.children
                .filter((child) => {
                    const info = availableBins[child.key];
                    return info && (info.deckCount ?? 0) > 0;
                })
                .map((child) => ({ ...child, deckCount: availableBins[child.key]?.deckCount ?? 0 })),
        })).filter((section) => section.children.length > 0);
    }, [health, availableBins]);

    const availableBinKeys = useMemo(() => {
        const keys = new Set();
        availableBinSections.forEach((s) => s.children.forEach((c) => keys.add(c.key)));
        return keys;
    }, [availableBinSections]);

    // Snap URL/selection to a valid bin once health lands. If the URL's bin
    // isn't in availableBins, prefer the rating-derived bracket, else fall
    // back to other brackets, else the first available key.
    const effectiveBin = useMemo(() => {
        if (!health || availableBinKeys.size === 0) return null;
        if (availableBinKeys.has(binParam)) return binParam;
        const preferred = [smartDefaultBin, 'bracket_4', 'bracket_5', 'bracket_3', 'bracket_2', 'bracket_1', 'overall'];
        return preferred.find((k) => availableBinKeys.has(k)) || availableBinSections[0]?.children[0]?.key || null;
    }, [health, availableBinKeys, binParam, smartDefaultBin, availableBinSections]);

    const healthReady =
        !!health &&
        health.status !== 'disabled' &&
        health.status !== 'pending' &&
        health.status !== 'below_threshold' &&
        health.eligible;

    // Dispatch bin fetch only when health is ready and we have a valid bin.
    useEffect(() => {
        if (!commanderIndexId || !healthReady || !effectiveBin) return;
        if (storedCommanderId !== commanderIndexId || selectedBinKey !== effectiveBin) {
            dispatch(selectCommanderBin(commanderIndexId, effectiveBin));
        }
    }, [commanderIndexId, storedCommanderId, selectedBinKey, effectiveBin, healthReady, dispatch]);

    // Keep the URL param in sync when we snap to a different bin.
    useEffect(() => {
        if (!effectiveBin) return;
        if (binParam !== effectiveBin) setBinParam(effectiveBin);
    }, [effectiveBin, binParam, setBinParam]);

    const handleBinChange = (key) => {
        if (!commanderIndexId) return;
        if (!availableBinKeys.has(key)) return;
        dispatch(selectCommanderBin(commanderIndexId, key));
        setBinParam(key);
    };

    const response = responsesByBin[selectedBinKey];
    const isFetching = Boolean(fetchingBins[selectedBinKey]);
    const bin = response?.bin || null;

    const pickerSections = availableBinSections.length > 0 ? availableBinSections : BIN_SECTIONS;
    const pickerDisabled = !healthReady;

    const renderShell = (body) => (
        <View UNSAFE_className="commanderCardStatsPanel" padding={{ base: '8px', S: '20px' }}>
            <Flex direction="column" rowGap={15}>
                <Flex direction="row" alignItems="center" justifyContent="space-between" wrap rowGap={10}>
                    <Text UNSAFE_className="commanderCardStatsTitle">Deck composition stats</Text>
                    <Picker
                        label="Show stats for"
                        selectedKey={selectedBinKey}
                        onSelectionChange={handleBinChange}
                        items={pickerSections}
                        width={220}
                        isDisabled={pickerDisabled}
                    >
                        {(section) => (
                            <Section key={section.id} items={section.children} title={section.name}>
                                {(option) => <Item key={option.key}>{option.label}</Item>}
                            </Section>
                        )}
                    </Picker>
                </Flex>
                {body}
            </Flex>
        </View>
    );

    if (!commanderIndexId) return null;

    // Health probe hasn't landed yet.
    if (!health) {
        return renderShell(<SearedLoader size="large" subcaption={healthIsFetching ? 'Checking availability…' : ''} />);
    }

    if (health.status === 'disabled' || isDisabled) {
        return renderShell(
            <Text UNSAFE_className="commanderCardStatsEmpty">
                Deck composition stats are temporarily unavailable. Check back soon.
            </Text>
        );
    }

    if (health.status === 'pending') {
        return renderShell(
            <SearedLoader
                size="large"
                caption="Crunching the numbers"
                subcaption="Stats are being computed for this commander. Check back in a few minutes."
            />
        );
    }

    if (health.status === 'below_threshold' || !health.eligible) {
        return renderShell(
            <Text UNSAFE_className="commanderCardStatsEmpty">
                {`Not enough decks yet to compute stats — we need at least ${
                    health.threshold || 50
                } decks for this commander.`}
            </Text>
        );
    }

    if (availableBinKeys.size === 0) {
        return renderShell(<Text UNSAFE_className="commanderCardStatsEmpty">No bins with enough data yet.</Text>);
    }

    if (isFetching && !response) {
        return renderShell(<SearedLoader size="large" subcaption="Loading deck composition stats…" />);
    }

    if (!response || !bin) {
        return null;
    }

    const sampleSizeLabel =
        bin.deckCount > 0
            ? `${bin.deckCount.toLocaleString()} deck${bin.deckCount === 1 ? '' : 's'} analyzed in this bucket`
            : 'No decks in this bucket yet';

    return renderShell(
        <>
            <Flex direction="row" alignItems="center" columnGap={10} wrap>
                <Text UNSAFE_className="commanderCardStatsSampleSize">{sampleSizeLabel}</Text>
                {isFetching && <ProgressCircle size="S" aria-label="Refreshing" isIndeterminate />}
            </Flex>
            {bin.deckCount === 0 ? (
                <Text UNSAFE_className="commanderCardStatsEmpty">Nothing to show yet.</Text>
            ) : (
                <DeepLinkTabs defaultSelectedKey="cards" isEmphasized UNSAFE_style={{ padding: 0 }}>
                    <TabList marginTop="20px" marginStart="10px">
                        <Item key="archetypes">
                            <HotFixes size={'XS'} />
                            Archetypes
                        </Item>
                        <Item key="cards">
                            <Card size={'XS'} />
                            Cards
                        </Item>
                        <Item key="combos">
                            <FlashOn size={'XS'} />
                            Combos
                        </Item>
                        <Item key="manabase">
                            <ColorPalette size={'XS'} />
                            Manabase
                        </Item>
                        <Item key="salt">
                            <Beaker size={'XS'} />
                            Saltiness
                        </Item>
                        <Item key="synergy">
                            <DataMapping size={'XS'} />
                            Synergy
                        </Item>
                    </TabList>
                    <TabPanels marginTop="10px">
                        <Item key="archetypes">
                            <ArchetypesTab bin={bin} onSelectCard={handleSelectCard} />
                        </Item>
                        <Item key="cards">
                            <CardsTab bin={bin} onSelectCard={handleSelectCard} />
                        </Item>
                        <Item key="combos">
                            <CombosTab bin={bin} onSelectCard={handleSelectCard} />
                        </Item>
                        <Item key="manabase">
                            <ManabaseTab bin={bin} onSelectCard={handleSelectCard} />
                        </Item>
                        <Item key="salt">
                            <SaltTab bin={bin} onSelectCard={handleSelectCard} />
                        </Item>
                        <Item key="synergy">
                            <SynergyTab bin={bin} onSelectCard={handleSelectCard} />
                        </Item>
                    </TabPanels>
                </DeepLinkTabs>
            )}
        </>
    );
}
